import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { subjects, isBlindedStudyType, type StudyBlindingType } from '@/api/client';
import { Modal } from '@/components/ui/Modal';
import { useHasRole } from '@/components/RoleGate';
import { errorText } from '@/lib/permissionError';

/**
 * Punch-list DT2-BLIND-4 — Emergency Unblind.
 *
 * ICH E6(R3) §2.11: "Breaking of the blind for individual patients in
 * emergency situations is an Investigator responsibility." This records
 * the AUDITED EVENT of an emergency unblinding — DreamTrials doesn't
 * store treatment-arm codes (those live in the study's IRT/IWRS system),
 * so there is nothing to "reveal" here beyond the fact that it happened,
 * who did it, and why.
 *
 * Deliberately rendered as its own visible, labeled button rather than
 * tucked inside the "…" status-actions menu — a rare, high-consequence
 * safety action shouldn't share an unlabeled overflow trigger with
 * routine status transitions.
 */
const EMERGENCY_UNBLIND_ROLES = [
  'principal_investigator', 'sub_investigator',
  'site_admin', 'platform_admin', 'application_admin',
] as const;

export function EmergencyUnblindAction({
  subjectId,
  studyBlindingType,
}: {
  subjectId: string;
  studyBlindingType?: StudyBlindingType | null;
}) {
  const qc = useQueryClient();
  const canAct = useHasRole(EMERGENCY_UNBLIND_ROLES);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const mutation = useMutation({
    mutationFn: () => subjects.emergencyUnblind(subjectId, { reason: reason.trim() }),
    onSuccess: (res) => {
      toast.success(
        res.sponsor_notified
          ? 'Emergency unblinding recorded — sponsor notified.'
          : 'Emergency unblinding recorded. Sponsor contact not on file — notify them directly.',
      );
      setOpen(false);
      setReason('');
      qc.invalidateQueries({ queryKey: ['subject', subjectId] });
    },
    onError: (err) => toast.error(errorText(err, 'Failed to record emergency unblinding')),
  });

  // Only makes sense for a study that's actually blinded — nothing to
  // unblind on an Open-Label (or unclassified) study. Checked after all
  // hooks so this component stays hook-order-safe.
  if (!isBlindedStudyType(studyBlindingType)) return null;
  if (!canAct) return null;

  const isValid = reason.trim().length >= 10;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded border border-danger-300 bg-danger-50 px-2.5 py-1.5 text-xs font-medium text-danger-700 hover:bg-danger-100"
        title="Record an emergency unblinding event for this subject"
      >
        <AlertOctagon className="h-3.5 w-3.5" /> Emergency Unblind
      </button>

      <Modal
        open={open}
        onOpenChange={(b) => {
          if (!b && !mutation.isPending) {
            setOpen(false);
            setReason('');
          }
        }}
        title="Emergency Unblind"
        description="Records an audited emergency-unblinding event for this subject. DreamTrials does not store treatment-arm codes — the actual decode must still be obtained through the study's IRT/IWRS system. The sponsor's contact is notified if one is on file."
        width={520}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
          className="space-y-4"
        >
          <div>
            <label className="label text-xs" htmlFor="emergency-unblind-reason">
              Reason (required)
            </label>
            <textarea
              id="emergency-unblind-reason"
              className="input min-h-[100px]"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              minLength={10}
              maxLength={2000}
              required
              placeholder="Describe the safety emergency requiring unblinding, e.g. a serious adverse event requiring the treating physician to know the assigned treatment."
            />
            <p className="mt-1 text-[10px] text-ink-muted">
              At least 10 characters. Recorded verbatim on the audit event — visible to inspectors.
            </p>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn-ghost"
              disabled={mutation.isPending}
              onClick={() => {
                setOpen(false);
                setReason('');
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-danger" disabled={!isValid || mutation.isPending}>
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Record emergency unblinding
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
